// Owner membership hooks (SLICE-MEMBERSHIP-PASS). Powers the customer-detail
// membership section: load the card detail (which carries `membership_context`
// for membership cards), change the member's tier, and renew the term.
//
// The verify-scan check-in is NOT here — that's driven inline by the scanner
// flows (owner DashboardPage + staff overlay) where the scanned token lives.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ownerApi } from '../ownerApi.js'
import type { MembershipContext } from '../../shared/membership/membershipTypes.js'

/** GET /api/cards/:id — the membership-relevant slice, plus the loyalty
 *  "more info" stats the Customers-page detail modal shows. */
export interface CardDetailResponse {
  card: {
    id: string
    card_code: string
    status: string
    pass_type: 'loyalty' | 'membership' | 'event_ticket'
    stamps_count: number
    /** Cycle index — doubles as "cards completed" (see completions_count in
     *  CLAUDE.md). Always 0 for membership/event-ticket cards. */
    completions_count: number
    /** Customer lifecycle (2026-09-05) — null for membership/event-ticket
     *  cards, which have no stamp total. The single source of truth for
     *  "how many stamps total" — don't pass a separately-sourced value in
     *  from a list/search row, which can drift. */
    total_stamps: number | null
  }
  /** Customer lifecycle (2026-09-05) — drives the "adjust secret stamps"
   *  control in the stamp-adjustment form. Null for non-loyalty cards;
   *  `enabled` false means secret-menu tagging isn't currently on for this
   *  card's tenant/program (the control should be hidden, not just
   *  disabled). `count` is the current cycle's secret-tagged stamp count;
   *  `required` is the owner-configured "secret stamps needed" threshold
   *  (for the "x of <required>" stat), separate from total_stamps. `tags`
   *  is every current-cycle row's is_secret_menu flag, ordered by
   *  stamp_number ascending — the adjustment form needs this (not just the
   *  aggregate count) to predict how many secret-tagged rows a total-count
   *  DECREASE would remove (it deletes the highest stamp_number rows
   *  first, which may themselves be secret-tagged). */
  secret_menu_adjustable: {
    enabled: boolean
    count: number
    required: number | null
    tags: boolean[]
  } | null
  customer: {
    id: string
    first_name: string | null
    // Customer lifecycle (2026-09-05) — the oldest possible timeline entry
    // in the Activity section ("Customer enrolled on X").
    created_at: string
    // Customer lifecycle (2026-09-05) — drives the Customers-page danger
    // zone. deactivated_at is the reversible revoke; erased_at is the
    // irreversible RA 10173 erasure (which also implies revoked).
    deactivated_at: string | null
    deactivation_reason: string | null
    erased_at: string | null
    erasure_requested_via: string | null
    // Follow-up (2026-09-05) — who performed the revoke/erase, resolved to a
    // display name server-side (audit_log doesn't capture a plain owner
    // action, so this is the only "who" record for these two).
    revoked_by_name: string | null
    erased_by_name: string | null
  } | null
  // Null for loyalty cards; populated for membership cards.
  membership_context: MembershipContext | null
  // Zero/null defaults on membership + event-ticket cards, which have no
  // stamps or reward redemptions.
  lifetime_stamps_count: number
  last_stamp_at: string | null
  rewards_claimed_count: number
  last_redemption: { reward_text: string; redeemed_at: string } | null
}

export const cardDetailKey = (cardId: string) =>
  ['owner', 'card-detail', cardId] as const

/**
 * Load a single card's detail. Used by the customer-detail drawer to decide
 * loyalty-vs-membership UI and to render the membership section.
 */
export function useCardDetail(cardId: string | null) {
  return useQuery<CardDetailResponse>({
    queryKey: cardDetailKey(cardId ?? ''),
    enabled: !!cardId,
    queryFn: () =>
      ownerApi.get<CardDetailResponse>(`/cards/${encodeURIComponent(cardId!)}`),
  })
}

/**
 * Change a membership card's tier (owner-only). `tier_name: null` clears the
 * per-card override back to the program tier. The server pushes the change to
 * both wallets. Invalidates the card detail so the new tier shows immediately.
 */
export function useChangeMembershipTier(cardId: string) {
  const qc = useQueryClient()
  return useMutation<{ card: unknown }, Error, string | null>({
    mutationFn: (tierName: string | null) =>
      ownerApi.patch<{ card: unknown }>(
        `/cards/${encodeURIComponent(cardId)}/membership`,
        { tier_name: tierName },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardDetailKey(cardId) })
    },
  })
}

/**
 * Renew a membership's term (owner-only). 422 MEMBERSHIP_NOT_RENEWABLE if the
 * program has no relative term — the caller surfaces that gracefully. Returns
 * the new expiry. Invalidates the card detail.
 */
export function useRenewMembership(cardId: string) {
  const qc = useQueryClient()
  return useMutation<{ expires_at: string | null }, Error, void>({
    mutationFn: () =>
      ownerApi.post<{ card: unknown; expires_at: string | null }>(
        `/cards/${encodeURIComponent(cardId)}/membership/renew`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardDetailKey(cardId) })
    },
  })
}
